// Floating, resizable, non-modal window showing a single TRQ related
// document's full detail (INVOICE/RMA/REPLACEMENT/BORD/PFR/RETURN) — every
// channel/trqClientCode as of 2026-09-03 (previously 1AW/AMW only), opened
// from the eye icon on a Related Documents row (App.jsx). Read-only in this
// pass: no action buttons.
//
// Unlike OrderNotesWindow, this needs no fetch of its own for the header/
// shipping/tax/items fields — `doc` already carries all of it (routes/
// conversations.js's relatedDocuments mapping projects these straight off
// the same TRQ record already embedded in the parent order response). Only
// the Notes section fetches on open, via the shared NotesList.jsx.
import { useState } from 'react'
import { Box, Typography, Stack, Chip, Divider, Table, TableHead, TableBody, TableRow, TableCell, TableContainer } from '@mui/material'
import DraggableModal from './DraggableModal.jsx'
import NotesList from './NotesList.jsx'
import { AddressBlock } from './AddressBlock.jsx'
import { CopyButton } from './CopyButton.jsx'
import { groupItemsForTable } from './SuggestionPanel.jsx'

// Same currency-formatting convention as App.jsx's own money() (and
// CostView.jsx's) — duplicated rather than imported, same "small pure
// helper duplicated across independent files" convention already
// established elsewhere in this codebase (e.g. server/src's classifyItem
// duplicated across enrichment.js/return-refund.js/replacement.js).
function money(v, currency = 'USD') {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(v)
  } catch {
    return `$${Number(v).toFixed(2)}`
  }
}

// "Today"/"1 day ago"/"N days ago" — deliberately NOT App.jsx's own
// orderAgeInfo(), which bakes in a 60-day red/green risk-coloring rule
// that's specific to judging a PARENT order's age, not relevant to a
// document detail popup — this is a plain, always-neutral informational
// label. Same whole-calendar-day, business-timezone date math as
// orderAgeInfo() though, so "Today" lines up with the same day boundary.
function daysAgoLabel(dateIso) {
  if (!dateIso) return null
  const date = new Date(dateIso)
  if (isNaN(date.getTime())) return null
  const toDayKey = (d) => d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
  const days = Math.max(0, Math.floor((new Date(`${toDayKey(new Date())}T00:00:00Z`) - new Date(`${toDayKey(date)}T00:00:00Z`)) / 86400000))
  if (days === 0) return 'Today'
  if (days === 1) return '1 day ago'
  return `${days} days ago`
}

const DEFAULT_WIDTH = 900
const DEFAULT_HEIGHT = 760
const CHIP_SX = { height: 20, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }

// One header field: label caption + value, with an optional copy button —
// same two-line label/value shape used throughout this widget. Renders
// nothing at all when the value is absent, so a row of these never leaves a
// visible gap for an unresolved/unconfirmed field.
function HeaderField({ label, value, copyValue }) {
  if (!value) return null
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontWeight: 700 }}>{label}</Typography>
      <Stack direction="row" spacing={0.25} alignItems="center">
        <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary', overflowWrap: 'break-word' }}>{value}</Typography>
        {copyValue && <CopyButton value={copyValue} />}
      </Stack>
    </Box>
  )
}

// A much simpler dedicated table than ItemsTable/ItemDetailsRow (App.jsx) —
// just SKU/Description/Qty/Price, no tracking/stock/fit/tags — so this
// reuses only the visual CONVENTION those give component rows (indent + "↳"
// prefix), not the components themselves.
function DocumentItemsTable({ items }) {
  if (!items?.length) return null
  return (
    <TableContainer>
      <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>SKU</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Description</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Qty</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>Price</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it, i) => {
            const isComponent = it.itemType === 'component'
            return (
              <TableRow key={it.sku ?? `${it.description}-${i}`}>
                <TableCell sx={{ pl: isComponent ? 3 : 1, fontSize: 13 }}>
                  {isComponent && (
                    <Chip label="C" size="small" sx={{ height: 16, width: 16, mr: 0.5, fontSize: '0.6rem', fontWeight: 700, '& .MuiChip-label': { px: 0 } }} />
                  )}
                  {isComponent ? '↳ ' : ''}{it.sku ?? '—'}
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{it.description ?? '—'}</TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{it.quantity ?? '—'}</TableCell>
                <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>{it.unitPrice != null ? money(it.unitPrice) : '—'}</TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

// conversationId: which conversation's document this is (for the Notes
// fetch). doc: one entry from orderSummary.relatedDocuments.documents —
// already carries every field this window renders. offsetIndex: this
// window's position in the currently-open list, same stagger convention as
// notesWindows. onClose: called with no args when the CSR dismisses this
// window.
export default function DocumentDetailWindow({ conversationId, doc, offsetIndex = 0, onClose }) {
  const [noteCount, setNoteCount] = useState(0)
  if (!doc) return null

  const age = daysAgoLabel(doc.date)

  return (
    <DraggableModal
      open
      onClose={onClose}
      title={`${doc.documentType} — ${doc.erpOrderId}`}
      offsetIndex={offsetIndex}
      width={DEFAULT_WIDTH}
      height={DEFAULT_HEIGHT}
      minWidth={420}
      minHeight={400}
      contentSx={{ p: 1.5 }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" label={(doc.documentType ?? '').toUpperCase()} sx={{ ...CHIP_SX, fontWeight: 700 }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{doc.erpOrderId}</Typography>
          {doc.status && <Chip size="small" label={doc.status} color="info" variant="outlined" sx={CHIP_SX} />}
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <Box sx={{ gridColumn: '1 / span 2', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <HeaderField label="TRQ ID" value={doc.trqId} copyValue={doc.trqId} />
            <HeaderField label="ERP ID" value={doc.erpOrderId} copyValue={doc.erpOrderId} />
            <HeaderField label="PO Number" value={doc.poNumber} />
            <HeaderField label="Batch" value={doc.batch} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontWeight: 700 }}>Date</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>{doc.date ?? '—'}</Typography>
                {age && <Chip size="small" label={age} sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.6 } }} />}
              </Stack>
            </Box>
            <HeaderField label="Created By" value={doc.createdBy} />
            {doc.reasonCode && <HeaderField label="Reason Code" value={doc.reasonCode} />}
            {doc.fc && <HeaderField label="FC" value={doc.fc} />}
            {doc.parentRef && (
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2, fontWeight: 700 }}>Parent Ref</Typography>
                <Chip size="small" label={doc.parentRef} sx={CHIP_SX} />
              </Box>
            )}
          </Box>
          <Box sx={{ gridColumn: '3' }}>
            <AddressBlock heading="Shipping"
              name={doc.shipping?.name} address1={doc.shipping?.address1} address2={doc.shipping?.address2}
              city={doc.shipping?.city} region={doc.shipping?.region} postalCode={doc.shipping?.postalCode} />
            {doc.shipping?.method && (
              <Chip size="small" label={doc.shipping.method} sx={{ ...CHIP_SX, mt: 0.5 }} />
            )}
          </Box>
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="body2" sx={{ fontSize: 13, color: 'text.secondary' }}>
            Tax: {doc.orderTax != null ? money(doc.orderTax) : '—'}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{doc.orderTotal != null ? money(doc.orderTotal) : '—'}</Typography>
        </Stack>

        {/* RMA's computed "Refund" box (Items subtotal / Estimated tax /
            Refund Total) is deliberately NOT built here — see
            routes/conversations.js's own comment on why: no live TRQ
            response was available to confirm the RMA document's own
            computed-refund field names, and reconstructing that dollar
            figure client-side would risk showing a CSR a wrong number.
            Flagged back rather than guessed; add once confirmed. */}

        {doc.items?.length > 0 && (
          <>
            <Divider />
            <DocumentItemsTable items={groupItemsForTable(doc.items)} />
          </>
        )}

        <Divider />

        <Typography variant="caption" fontWeight={700} display="block">Notes ({noteCount})</Typography>
        <NotesList conversationId={conversationId} erpOrderId={doc.erpOrderId} onCountChange={setNoteCount} />
      </Stack>
    </DraggableModal>
  )
}
